import { memo, useState, useEffect, useRef } from 'react';
import { DateRangePicker as ReactDateRangePicker } from 'react-date-range';
import {
  defaultStaticRanges,
  defaultInputRanges,
} from 'react-date-range/dist/defaultRanges';
import { LuCalendarDays } from 'react-icons/lu';
import { RiCloseLine } from 'react-icons/ri';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import styles from './styles.module.css';

const FilterDateRangePicker = memo(
  ({
    onDateRangeChange,
    initialStartDate = null,
    initialEndDate = null,
    className,
    ...props
  }) => {
    const [selectionRange, setSelectionRange] = useState({
      startDate: initialStartDate,
      endDate: initialEndDate,
      key: 'selection',
    });
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
      setSelectionRange({
        startDate: initialStartDate,
        endDate: initialEndDate,
        key: 'selection',
      });
    }, [initialStartDate, initialEndDate]);

    const handleSelect = (ranges) => {
      setSelectionRange(ranges.selection);
      onDateRangeChange?.(ranges.selection);
    };

    const formatDate = (date) => {
      if (!date) return '';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    const togglePicker = () => {
      setIsOpen(!isOpen);
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const getButtonText = () => {
      if (!selectionRange.startDate || !selectionRange.endDate) {
        return 'Select Date Range';
      }
      return `${formatDate(selectionRange.startDate)} - ${formatDate(
        selectionRange.endDate
      )}`;
    };

    const isPlaceholder =
      !selectionRange.startDate || !selectionRange.endDate;

    const handleBackdropClick = () => {
      setIsOpen(false);
    };

    const pickerProps = {
      ranges: [selectionRange],
      onChange: handleSelect,
      months: 1,
      direction: 'horizontal',
      showPreview: true,
      staticRanges: defaultStaticRanges,
      inputRanges: defaultInputRanges,
      startDatePlaceholder: 'Start date',
      endDatePlaceholder: 'End date',
      ...props,
    };

    return (
      <div
        ref={containerRef}
        className={`${styles.container} ${className || ''}`}
      >
        <button
          className={styles.dateRangeButton}
          onClick={togglePicker}
          type="button"
        >
          <span
            className={`${styles.buttonText} ${
              isPlaceholder ? styles.buttonTextPlaceholder : ''
            }`}
          >
            {getButtonText()}
          </span>
          <LuCalendarDays className={styles.calendarIcon} />
        </button>
        {isOpen && (
          <div className={styles.modalOverlay}>
            <div
              className={styles.modalOverlayBackdrop}
              onClick={handleBackdropClick}
              aria-hidden="true"
            />
            <div className={styles.modalOverlayPanel}>
              <div className={styles.modalOverlayHeader}>
                <button
                  className={styles.closeButton}
                  onClick={handleBackdropClick}
                  type="button"
                  aria-label="Close"
                >
                  <RiCloseLine className={styles.closeIcon} />
                </button>
              </div>
              <div className={styles.modalPickerWrapper}>
                <ReactDateRangePicker {...pickerProps} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default FilterDateRangePicker;
